import mapboxgl from "mapbox-gl";
import * as THREE from "three";

/**
 * Mapbox 地圖管理器
 * 用於整合 Mapbox 地圖與 Three.js 3D 場景
 */
export class MapManager {
  private map: mapboxgl.Map | null = null;
  private mapContainer: HTMLElement;
  private sceneContainer: HTMLElement;
  private viewMode: "3d-only" | "map-only" | "split" | "overlay" = "split";
  private modelPosition: { lng: number; lat: number } | null = null;
  private modelMarker: mapboxgl.Marker | null = null;

  constructor(
    mapContainerId: string,
    sceneContainerId: string,
    mapboxToken: string,
    initialCenter: [number, number] = [0, 0],
    initialZoom: number = 2
  ) {
    const mapContainerEl = document.getElementById(mapContainerId);
    const sceneContainerEl = document.getElementById(sceneContainerId);

    if (!mapContainerEl) {
      throw new Error(`找不到地圖容器元素: #${mapContainerId}`);
    }
    if (!sceneContainerEl) {
      throw new Error(`找不到場景容器元素: #${sceneContainerId}`);
    }

    this.mapContainer = mapContainerEl;
    this.sceneContainer = sceneContainerEl;

    // 設置 Mapbox access token
    mapboxgl.accessToken = mapboxToken;

    // 初始化地圖
    this.map = new mapboxgl.Map({
      container: this.mapContainer, // 傳遞 HTMLElement 而不是字符串
      style: "mapbox://styles/mapbox/streets-v12", // 可以改為 satellite-v9 或其他樣式
      center: initialCenter,
      zoom: initialZoom,
      antialias: true,
    });

    this.map.on("load", () => {
      console.log("✅ Mapbox 地圖載入完成");
      console.log("📍 地圖中心:", this.map!.getCenter());
      console.log("🔍 地圖縮放:", this.map!.getZoom());
      
      // 確保地圖調整大小
      setTimeout(() => {
        this.map!.resize();
        console.log("✅ 地圖已調整大小");
      }, 100);
      
      this.updateViewMode();
    });
    
    // 添加錯誤處理
    this.map.on("error", (e) => {
      console.error("❌ 地圖載入錯誤:", e);
    });
    
    // 確保地圖容器可見
    this.mapContainer.style.display = "block";
    this.mapContainer.style.visibility = "visible";
    this.mapContainer.style.opacity = "1";
  }

  /**
   * 設置模型的地理位置
   */
  setModelLocation(lng: number, lat: number, modelName?: string) {
    this.modelPosition = { lng, lat };

    // 移除舊的標記
    if (this.modelMarker) {
      this.modelMarker.remove();
    }

    // 創建新的標記
    const el = document.createElement("div");
    el.className = "model-marker";
    el.style.width = "20px";
    el.style.height = "20px";
    el.style.borderRadius = "50%";
    el.style.backgroundColor = "#ff6b6b";
    el.style.border = "3px solid white";
    el.style.cursor = "pointer";
    el.title = modelName || "BIM Model";

    this.modelMarker = new mapboxgl.Marker(el)
      .setLngLat([lng, lat])
      .addTo(this.map!);

    // 如果地圖已載入，移動到模型位置
    if (this.map?.loaded()) {
      this.map.flyTo({
        center: [lng, lat],
        zoom: 15,
        duration: 2000,
      });
    } else {
      this.map?.on("load", () => {
        this.map!.flyTo({
          center: [lng, lat],
          zoom: 15,
          duration: 2000,
        });
      });
    }
  }

  /**
   * 更新視圖模式
   */
  updateViewMode() {
    const body = document.body;
    const fullScreen = body.querySelector(".full-screen") as HTMLElement;

    switch (this.viewMode) {
      case "3d-only":
        this.mapContainer.style.display = "none";
        this.sceneContainer.style.width = "100%";
        this.sceneContainer.style.pointerEvents = "auto";
        fullScreen?.classList.remove("split-view");
        break;

      case "map-only":
        this.mapContainer.style.display = "block";
        this.mapContainer.style.visibility = "visible";
        this.mapContainer.style.opacity = "1";
        this.mapContainer.style.width = "100%";
        this.mapContainer.style.left = "0";
        this.mapContainer.style.zIndex = "10";
        this.sceneContainer.style.display = "none";
        fullScreen?.classList.remove("split-view");
        // 確保地圖重新調整大小
        setTimeout(() => {
          if (this.map) {
            this.map.resize();
            console.log("✅ 僅地圖模式：地圖已調整大小");
          }
        }, 200);
        break;

      case "split":
        this.mapContainer.style.display = "block";
        this.mapContainer.style.visibility = "visible";
        this.mapContainer.style.opacity = "1";
        this.mapContainer.style.width = "50%";
        this.mapContainer.style.left = "0";
        this.mapContainer.style.zIndex = "1";
        this.sceneContainer.style.display = "block";
        this.sceneContainer.style.width = "50%";
        this.sceneContainer.style.left = "50%";
        this.sceneContainer.style.pointerEvents = "auto";
        this.sceneContainer.style.zIndex = "2";
        fullScreen?.classList.add("split-view");
        // 確保地圖重新調整大小
        setTimeout(() => {
          if (this.map) {
            this.map.resize();
            console.log("✅ 分屏模式：地圖已調整大小");
          }
        }, 200);
        break;

      case "overlay":
        this.mapContainer.style.display = "block";
        this.mapContainer.style.width = "100%";
        this.mapContainer.style.left = "0";
        this.sceneContainer.style.display = "block";
        this.sceneContainer.style.width = "100%";
        this.sceneContainer.style.left = "0";
        this.sceneContainer.style.pointerEvents = "none"; // 允許點擊穿透到地圖
        fullScreen?.classList.remove("split-view");
        // 確保地圖重新調整大小
        setTimeout(() => {
          if (this.map) {
            this.map.resize();
          }
        }, 100);
        break;
    }
  }

  /**
   * 切換視圖模式
   */
  setViewMode(mode: "3d-only" | "map-only" | "split" | "overlay") {
    this.viewMode = mode;
    this.updateViewMode();
  }

  /**
   * 獲取當前地圖實例
   */
  getMap(): mapboxgl.Map | null {
    return this.map;
  }

  /**
   * 銷毀地圖
   */
  destroy() {
    if (this.modelMarker) {
      this.modelMarker.remove();
    }
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  /**
   * 將 Three.js 世界座標轉換為地理座標
   * 這是一個示例函數，需要根據實際情況調整
   */
  worldToGeo(
    worldPos: THREE.Vector3,
    originGeo: [number, number] = [0, 0],
    scale: number = 111320 // 米到度的轉換（緯度方向）
  ): [number, number] {
    // 簡化的轉換（實際應用中需要考慮投影和座標系統）
    const lng = originGeo[0] + worldPos.x / scale;
    const lat = originGeo[1] + worldPos.z / scale;
    return [lng, lat];
  }

  /**
   * 將地理座標轉換為 Three.js 世界座標
   */
  geoToWorld(
    geo: [number, number],
    originGeo: [number, number] = [0, 0],
    scale: number = 111320
  ): THREE.Vector3 {
    const x = (geo[0] - originGeo[0]) * scale;
    const z = (geo[1] - originGeo[1]) * scale;
    return new THREE.Vector3(x, 0, z);
  }
}
